import {ManifestStop, Maybe, RouteOptimizationUnassignedDetail} from "../../../generated/graphql";
import {RouteOptimizationErrorCodes} from "../../../constants/constants";
import {TJobLinkItem} from "../../common/JobLinksRenderer";

/**
 * DP-3154 — the stop status code for New.
 *
 * Deliberately local rather than taken from JobStatusResolver: that map describes *job*
 * status, and jobStopStatus is *stop* status. They happen to agree on "N" today but are
 * separate vocabularies, so borrowing one for the other would couple them by accident.
 * JobStopStatus in StopUtils only enumerates the three supported codes (A, C, Q).
 */
const NEW_JOB_STOP_STATUS = "N";

export const excludeTimeWindowErrors = (details: RouteOptimizationUnassignedDetail[] | undefined) => {
  if (!details) return [];
  return details.filter(
    (detail) =>
      ![
        RouteOptimizationErrorCodes.TimeWindowError,
        RouteOptimizationErrorCodes.GroupRelationConstraint,
        RouteOptimizationErrorCodes.RelationContaint
      ].includes(detail.code as any)
  );
};

export const extractAddressFromMessage: (input: string) => string = (input) => {
  const regex = /\[([^\]]+)\]/;
  const match = input.match(regex);
  if (match && match[1]) {
    return match[1];
  } else {
    return "";
  }
};

/**
 * DP-3154 — jobs owning a stop in New ("N") status, for the optimization error message.
 *
 * Matches "N" exactly rather than reusing isHasUnsupportedStatus, which is the negation of
 * {A, C, Q} and so also matches X/Z (deleted), I/P/V/E/T (other) and a null status. The
 * message asserts the stop is in 'new' status, so a broader predicate would state something
 * false. The manifest card's caution icon stays on the broader predicate by design.
 *
 * Deduplicated by jobId: one job can contribute both a pickup and a delivery stop, and the
 * message lists jobs rather than stops.
 *
 * orderId comes from stop.order, never from stop.job — navigateToCmsOrder puts it into the
 * CMS OrderID parameter, which is the internal order key. jobNumber and orderId are
 * independent sequences that overlap on some datasets, so reading the wrong one opens an
 * unrelated order rather than failing (DP-3150).
 */
export const findJobsWithNewStatusStops = (stops: Maybe<readonly ManifestStop[]> | undefined): TJobLinkItem[] => {
  const byJobId = new Map<number, TJobLinkItem>();
  for (const stop of stops ?? []) {
    if (stop.jobStopStatus !== NEW_JOB_STOP_STATUS) continue;
    if (byJobId.has(stop.job.jobId)) continue;
    byJobId.set(stop.job.jobId, {
      jobNumber: stop.job.jobNumber,
      jobId: stop.job.jobId,
      orderId: stop.order.orderId
    });
  }
  return [...byJobId.values()];
};
